import React, { useState } from "react";
import {
  MoreVertical,
  Pencil,
  Trash2,
  Wallet,
  LogOut,
  PhoneCall,
  MessageSquare,
  History,
  Home,
  XCircle,
  StickyNote,
} from "lucide-react";
import DropdownMenu from "./DropdownMenu";
import ConfirmDialog from "./ConfirmDialog";
import StatusBadge from "./StatusBadge";
import RecordPaymentDialog from "./RecordPaymentDialog";
import SmsDialog from "./SmsDialog";
import AiCallDialog from "./AiCallDialog";
import NoteDialog from "./NoteDialog";
import { useData } from "../context/DataContext";
import {
  formatKES,
  computeTenantStatus,
  tenantDueDay,
  tenantBalance,
  paidForMonth,
  currentMonth,
  utilityChargeForMonth,
  utilityBalanceForMonth,
} from "../utils/format";
import { Droplets, Zap } from "lucide-react";

// Shared tenants table used by /tenants, /properties/:id, and /rent.
// Props:
//  tenants, showProperty, onEdit, onOpenHistory
//  month: YYYY-MM string - which month's status to show (default current)
//  showAmountColumn: boolean - show "Paid / Due" column (for RentStatus)
export default function TenantsTable({
  tenants,
  showProperty = true,
  onEdit,
  onOpenHistory,
  month,
  showAmountColumn = false,
}) {
  const { properties, payments, utilityCharges, deleteTenant, vacateTenant, voidMonthPayments } = useData();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmVacate, setConfirmVacate] = useState(null);
  const [confirmVoid, setConfirmVoid] = useState(null);
  const [recordFor, setRecordFor] = useState(null);
  const [smsFor, setSmsFor] = useState(null);
  const [callFor, setCallFor] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [noteFor, setNoteFor] = useState(null);

  const ctxMonth = month || currentMonth();

  return (
    <>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <Th>Tenant</Th>
                {showProperty ? <Th>Property · House</Th> : <Th>House</Th>}
                <Th>Rent</Th>
                {showAmountColumn ? <Th>Paid / Due</Th> : <Th>Due day</Th>}
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {tenants.map((t) => {
                const property = properties.find((p) => p.id === t.propertyId);
                const status = computeTenantStatus(t, payments, ctxMonth);
                const isActive = t.status === "ACTIVE";
                const paid = paidForMonth(payments, t.id, ctxMonth);
                const balance = tenantBalance(t, payments, ctxMonth);
                return (
                  <tr key={t.id}>
                    <Td className="font-medium text-gray-900 dark:text-white">
                      {t.name}
                      <div className="text-xs font-normal text-gray-500">{t.phone || "—"}</div>
                    </Td>
                    {showProperty ? (
                      <Td>{property?.name || "—"} · {t.houseNumber}</Td>
                    ) : (
                      <Td>{t.houseNumber}</Td>
                    )}
                    <Td>{formatKES(t.rent)}</Td>
                    {showAmountColumn ? (
                      <Td>
                        <div className="text-gray-900 dark:text-white">{formatKES(paid)}</div>
                        {balance > 0 && (
                          <div className="text-xs text-red-600">
                            {formatKES(balance)} due
                          </div>
                        )}
                        {property?.waterEnabled && (() => {
                          const wc = utilityChargeForMonth(utilityCharges, t.id, ctxMonth, "water");
                          const wb = utilityBalanceForMonth(utilityCharges, payments, t.id, ctxMonth, "water");
                          return wc > 0 ? (
                            <div className="mt-1 flex items-center gap-1 text-[11px] text-blue-600 dark:text-blue-400">
                              <Droplets className="h-3 w-3" />
                              {wb > 0 ? `${formatKES(wb)} due` : "Paid"}
                            </div>
                          ) : null;
                        })()}
                        {property?.electricityEnabled && (() => {
                          const ec = utilityChargeForMonth(utilityCharges, t.id, ctxMonth, "electricity");
                          const eb = utilityBalanceForMonth(utilityCharges, payments, t.id, ctxMonth, "electricity");
                          return ec > 0 ? (
                            <div className="mt-0.5 flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                              <Zap className="h-3 w-3" />
                              {eb > 0 ? `${formatKES(eb)} due` : "Paid"}
                            </div>
                          ) : null;
                        })()}
                      </Td>
                    ) : (
                      <Td>{ordinal(tenantDueDay(t))} of each month</Td>
                    )}
                    <Td>
                      <StatusBadge status={status.toLowerCase()} />
                      {status === "PARTIAL" && (
                        <div className="mt-0.5 text-[11px] text-gray-500">
                          {formatKES(balance)} balance
                        </div>
                      )}
                      {property?.waterEnabled && (() => {
                        const wc = utilityChargeForMonth(utilityCharges, t.id, ctxMonth, "water");
                        if (!wc) return null;
                        const wb = utilityBalanceForMonth(utilityCharges, payments, t.id, ctxMonth, "water");
                        return (
                          <div className={`mt-1 flex items-center gap-1 text-[11px] ${wb > 0 ? "text-blue-600 dark:text-blue-400" : "text-green-600 dark:text-green-400"}`}>
                            <Droplets className="h-3 w-3" />
                            {wb > 0 ? `Water: ${formatKES(wb)} due` : "Water: Paid"}
                          </div>
                        );
                      })()}
                      {property?.electricityEnabled && (() => {
                        const ec = utilityChargeForMonth(utilityCharges, t.id, ctxMonth, "electricity");
                        if (!ec) return null;
                        const eb = utilityBalanceForMonth(utilityCharges, payments, t.id, ctxMonth, "electricity");
                        return (
                          <div className={`mt-0.5 flex items-center gap-1 text-[11px] ${eb > 0 ? "text-amber-600 dark:text-amber-400" : "text-green-600 dark:text-green-400"}`}>
                            <Zap className="h-3 w-3" />
                            {eb > 0 ? `Elec: ${formatKES(eb)} due` : "Elec: Paid"}
                          </div>
                        );
                      })()}
                    </Td>
                    <Td className="text-right">
                      <DropdownMenu
                        align="right"
                        trigger={(toggle, open) => (
                          <button
                            onClick={toggle}
                            className={`rounded-md p-1.5 text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 ${
                              open ? "bg-gray-100 text-gray-900" : ""
                            }`}
                            aria-label="Row actions"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        )}
                        items={[
                          isActive && {
                            label: "Record payment",
                            icon: Wallet,
                            onClick: () => setRecordFor(t),
                          },
                          isActive && paid > 0 && {
                            label: "Void this month's payments",
                            icon: XCircle,
                            onClick: () => setConfirmVoid({ tenant: t, month: ctxMonth }),
                          },
                          isActive && {
                            label: "Send SMS reminder",
                            icon: MessageSquare,
                            onClick: () => setSmsFor(t),
                          },
                          isActive && {
                            label: "AI call reminder",
                            icon: PhoneCall,
                            onClick: () => setCallFor(t),
                          },
                          isActive && {
                            label: "Add note",
                            icon: StickyNote,
                            onClick: () => setNoteFor(t),
                          },
                          isActive && { divider: true },
                          isActive && {
                            label: "Edit tenant",
                            icon: Pencil,
                            onClick: () => onEdit?.(t),
                          },
                          {
                            label: "Tenant history",
                            icon: History,
                            onClick: () => onOpenHistory?.({ scope: "tenant", tenant: t }),
                          },
                          {
                            label: "House unit history",
                            icon: Home,
                            onClick: () => onOpenHistory?.({ scope: "house", tenant: t }),
                          },
                          isActive && { divider: true },
                          isActive && {
                            label: "Vacate tenant",
                            icon: LogOut,
                            onClick: () => setConfirmVacate(t),
                          },
                          {
                            label: "Delete tenant",
                            icon: Trash2,
                            tone: "danger",
                            onClick: () => setConfirmDelete(t),
                          },
                        ].filter(Boolean)}
                      />
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <RecordPaymentDialog
        open={!!recordFor}
        onClose={() => setRecordFor(null)}
        tenant={recordFor}
        defaultMonth={ctxMonth}
      />

      <SmsDialog open={!!smsFor} onClose={() => setSmsFor(null)} tenant={smsFor} />
      <AiCallDialog open={!!callFor} onClose={() => setCallFor(null)} tenant={callFor} />
      <NoteDialog open={!!noteFor} onClose={() => setNoteFor(null)} tenant={noteFor} />

      <ConfirmDialog
        open={!!confirmVoid}
        onClose={() => setConfirmVoid(null)}
        title="Void this month's payments?"
        description="All payments recorded for this tenant in the current month will be removed."
        confirmLabel="Void payments"
        tone="danger"
        loading={actionLoading}
        onConfirm={async () => {
          setActionLoading(true);
          try { await voidMonthPayments(confirmVoid.tenant.id, confirmVoid.month); } catch {}
          setActionLoading(false);
          setConfirmVoid(null);
        }}
      />

      <ConfirmDialog
        open={!!confirmVacate}
        onClose={() => setConfirmVacate(null)}
        title={confirmVacate ? `Vacate ${confirmVacate.name}?` : ""}
        description="This marks the tenant as VACATED and frees up their house unit."
        confirmLabel="Vacate tenant"
        tone="danger"
        loading={actionLoading}
        onConfirm={async () => {
          setActionLoading(true);
          try { await vacateTenant(confirmVacate.id); } catch {}
          setActionLoading(false);
          setConfirmVacate(null);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete ? `Delete ${confirmDelete.name}?` : ""}
        description="This permanently removes the tenant from your records. This cannot be undone."
        confirmLabel="Delete tenant"
        tone="danger"
        loading={actionLoading}
        onConfirm={async () => {
          setActionLoading(true);
          try { await deleteTenant(confirmDelete.id); } catch {}
          setActionLoading(false);
          setConfirmDelete(null);
        }}
      />

    </>
  );
}

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return (
    <td className={`whitespace-nowrap px-5 py-3 text-sm text-gray-700 dark:text-gray-300 ${className}`}>
      {children}
    </td>
  );
}
